import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, AlertCircle } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const { t } = useTranslation('legal');

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-purple-50/60">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-8 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">{t('backToHome')}</span>
        </Link>

        {/* Header */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{t('privacy.title')}</h1>
              <p className="text-gray-600 mt-2">{t('privacy.lastUpdated')}</p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-900">{t('privacy.intro')}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl p-8 space-y-8">

          {/* 1. Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section1.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section1.intro')}</p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('privacy.section1.subsection1.title')}</h3>
                <ul className="space-y-2 list-disc list-inside">
                  <li>{t('privacy.section1.subsection1.item1')}</li>
                  <li>{t('privacy.section1.subsection1.item2')}</li>
                  <li>{t('privacy.section1.subsection1.item3')}</li>
                  <li>{t('privacy.section1.subsection1.item4')}</li>
                  <li>{t('privacy.section1.subsection1.item5')}</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('privacy.section1.subsection2.title')}</h3>
                <ul className="space-y-2 list-disc list-inside">
                  <li>{t('privacy.section1.subsection2.item1')}</li>
                  <li>{t('privacy.section1.subsection2.item2')}</li>
                  <li>{t('privacy.section1.subsection2.item3')}</li>
                  <li>{t('privacy.section1.subsection2.item4')}</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 2. How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section2.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section2.intro')}</p>
              <ul className="space-y-2 list-disc list-inside ml-4">
                <li>{t('privacy.section2.item1')}</li>
                <li>{t('privacy.section2.item2')}</li>
                <li>{t('privacy.section2.item3')}</li>
                <li>{t('privacy.section2.item4')}</li>
                <li>{t('privacy.section2.item5')}</li>
                <li>{t('privacy.section2.item6')}</li>
                <li>{t('privacy.section2.item7')}</li>
              </ul>
            </div>
          </section>

          {/* 3. Data Sharing & Disclosure */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section3.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section3.intro')}</p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{t('privacy.section3.recruiters.title')}</h3>
                <p>{t('privacy.section3.recruiters.description')}</p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{t('privacy.section3.serviceProviders.title')}</h3>
                <p>{t('privacy.section3.serviceProviders.description')}</p>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>{t('privacy.section3.serviceProviders.item1')}</li>
                  <li>{t('privacy.section3.serviceProviders.item2')}</li>
                  <li>{t('privacy.section3.serviceProviders.item3')}</li>
                  <li>{t('privacy.section3.serviceProviders.item4')}</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{t('privacy.section3.legal.title')}</h3>
                <p>{t('privacy.section3.legal.description')}</p>
              </div>
            </div>
          </section>

          {/* 4. Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section4.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section4.p1')}</p>
              <ul className="space-y-2 list-disc list-inside ml-4">
                <li>{t('privacy.section4.item1')}</li>
                <li>{t('privacy.section4.item2')}</li>
                <li>{t('privacy.section4.item3')}</li>
                <li>{t('privacy.section4.item4')}</li>
              </ul>
              <p>{t('privacy.section4.p2')}</p>
            </div>
          </section>

          {/* 5. Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section5.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section5.p1')}</p>
              <p>{t('privacy.section5.p2')}</p>
            </div>
          </section>

          {/* 6. Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section6.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section6.intro')}</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{t('privacy.section6.right1.title')}</h3>
                  <p className="text-sm">{t('privacy.section6.right1.description')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('privacy.section6.right2.title')}</h3>
                  <p className="text-sm">{t('privacy.section6.right2.description')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('privacy.section6.right3.title')}</h3>
                  <p className="text-sm">{t('privacy.section6.right3.description')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('privacy.section6.right4.title')}</h3>
                  <p className="text-sm">{t('privacy.section6.right4.description')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('privacy.section6.right5.title')}</h3>
                  <p className="text-sm">{t('privacy.section6.right5.description')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('privacy.section6.right6.title')}</h3>
                  <p className="text-sm">{t('privacy.section6.right6.description')}</p>
                </div>
              </div>
              <p>{t('privacy.section6.exercise')}</p>
            </div>
          </section>

          {/* 7. Cookies & Tracking */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section7.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section7.p1')}</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">{t('privacy.section7.types.title')}</h3>
                <ul className="space-y-2 list-disc list-inside">
                  <li>{t('privacy.section7.types.item1')}</li>
                  <li>{t('privacy.section7.types.item2')}</li>
                  <li>{t('privacy.section7.types.item3')}</li>
                </ul>
              </div>
              <p>{t('privacy.section7.p2')}</p>
            </div>
          </section>

          {/* 8. International Data Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section8.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section8.p1')}</p>
            </div>
          </section>

          {/* 9. Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section9.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section9.p1')}</p>
            </div>
          </section>

          {/* 10. Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section10.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section10.p1')}</p>
            </div>
          </section>

          {/* 11. Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.section11.title')}</h2>
            <div className="space-y-4 text-gray-700">
              <p>{t('privacy.section11.intro')}</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="font-medium text-gray-900">{t('privacy.section11.companyName')}</p>
                <p>{t('privacy.section11.address')}</p>
                <p>{t('privacy.section11.email')}</p>
                <p>{t('privacy.section11.privacyEmail')}</p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-6 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-6 text-center">
          <p className="text-gray-600">
            {t('privacy.footer')}{' '}
            <Link to="/terms" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
              {t('termsOfServiceLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
